import { defineStore } from 'pinia'
import { APIS, LOADERS } from 'src/config/constants'
import { useLoader } from 'src/hooks/useLoader'
import type { ICenter } from 'src/interfaces/center'
import type { IOrgInfo, IOrgInfoWithToken } from 'src/interfaces/org'
import { fail } from 'src/utils/notify'
import type { LastSessionInfo} from 'src/common/store';
import { useAuthStore } from 'src/common/store'

export interface IOrgState {
  orgAlias: string
  orgName: string
  centers: ICenter[]
  orgInfoList: IOrgInfoWithToken[]
  selectedOrg?: IOrgInfoWithToken
  publicRegistrationOrg?: IOrgInfo
}

export const useOrgStore = defineStore('pinia/org', {
  state: () =>
    ({
      orgAlias: '',
      orgName: '',
      centers: [],
      orgInfoList: [],
    } as IOrgState),
  actions: {
    async getOrgInfoList() {
      const {
        useFetchLoader: fetchOrgInfoList,
        result: orgInfoList,
        error: fetchOrgInfoListError,
      } = useLoader<IOrgInfoWithToken[]>(LOADERS.res, { uri: APIS.orgInfoList })
      await fetchOrgInfoList()
      if (fetchOrgInfoListError.value) {
        fail(fetchOrgInfoListError.value.message)
        return
      }
      this.orgInfoList = orgInfoList.value ?? []

      if (!orgInfoList.value || orgInfoList.value.length === 0) {
        this.selectedOrg = undefined
        return
      }

      const authStore = useAuthStore()
      const orgInfo = orgInfoList.value?.find(
        (oInfo) => oInfo.id === authStore.currentOrgId
      )

      if (orgInfo) {
        this.selectedOrg = orgInfo
        return
      }
      this.selectedOrg = orgInfoList.value[0]
    },
    setSelectedOrg(org?: IOrgInfoWithToken) {
      this.selectedOrg = org
      const authStore = useAuthStore()
      authStore.setCurrentOrgId(org?.id)
      const lastSession: Partial<LastSessionInfo> = {
        lastOrgId: org?.id,
      }

      // If the current centerId is not in the new org's centers, reset to first center
      if (this.centers.length > 0) {
        authStore.setCurrentCenterId(this.centers[0].id)
        lastSession.lastCenterId = this.centers[0].id
      }
    },
    setOrgName(orgName: string) {
      this.orgName = orgName
    },
    setOrgCenters(centers: ICenter[]) {
      this.centers = centers
    },
    setPublicRegistrationOrg(org: IOrgInfo) {
      this.publicRegistrationOrg = org
    },
  },
})
